import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';

import type { IWeekListByMonth } from '@/store/types';
import type { IWeekBlocks } from '@/types/block';

import { useAppDispatch, useAppSelector, useTimezone } from '@/hooks';
import { getCurrentWeek } from '@/store/slices/utilsSlice';
import { CalendarDisplayRange, WeekDays } from '@/store/enums';
import { selectCurrentWeek, selectWeeks } from '@/store/selectors/utilsSelectors';
import { Block } from '@/classes/Block';

interface IReturn {
  currentWeek: number;
  getWeek: (weekId: number) => IWeekListByMonth | null;
  getWeekBlocks: (weekId?: number) => IWeekBlocks;
  selectedWeek: IWeekListByMonth | null;
  selectedDays: CalendarDisplayRange;
  selectedWeekId: number;
  selectedOffset: WeekDays | null;
  isCurrentWeek: boolean;
  selectDays: (days: CalendarDisplayRange) => void;
  selectWeek: (weekId: number) => void;
  selectStartDayIndex: (diff: number) => void;
  selectDayOffset: (offset: WeekDays) => void;
}

const useWeekSelector = createSelector(selectCurrentWeek, selectWeeks, (currentWeek, weeksListObject) => ({
  currentWeek,
  weeksListObject,
}));

const useWeek = (): IReturn => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { currentWeek, weeksListObject } = useAppSelector(useWeekSelector);
  const { timezoneName } = useTimezone();

  useEffect(() => {
    if (currentWeek) {
      return;
    }

    const promise = dispatch(getCurrentWeek());
    return () => promise.abort();
  }, [currentWeek, dispatch]);

  /**
   * Get week data for a given weekId.
   *
   * @param {number} weekId
   */
  const getWeek = useCallback(
    (weekId: number): IWeekListByMonth | null => weeksListObject[weekId] || null,
    [weeksListObject],
  );

  /**
   * Read current week data..
   *
   * @return {*}  {(IWeekListByMonth | null)}
   */
  const _getSelectedWeek = useMemo(() => getWeek(currentWeek), [currentWeek, getWeek]);

  /**
   * Read "weekId" from the URL query param.
   *
   * @return {*}  {(number | null)}
   */
  const _getWeekIdFromURL = useMemo((): number | null => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get('weekid');
    return queryParam ? Number(queryParam) : null;
  }, [location]);

  /**
   * Read "days" from the URL query param.
   *
   * @return {*}  {number}
   */
  const _getDaysFromURL = useMemo((): CalendarDisplayRange => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get('days');
    return queryParam ? Number(queryParam) : CalendarDisplayRange.WEEK;
  }, [location]);

  /**
   * Read "offset" from the URL query param.
   *
   * @return {*}  {number}
   */
  const _getOffsetFromURL = useMemo((): number | null => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get('offset');
    return queryParam ? Number(queryParam) : null;
  }, [location]);

  /**
   * Get startBlock & endBlock for a given weekId.
   *
   * @param {number} weekId
   */
  const getWeekBlocks = useCallback<IReturn['getWeekBlocks']>(
    (weekId: number = currentWeek) => {
      const startBlock = Block.getBlockIdByWeekId(weekId, timezoneName);
      const endBlock = Block.getBlockIdByWeekId(weekId, timezoneName, WeekDays.Sunday, 'end');

      return { startBlock, endBlock };
    },
    [currentWeek, timezoneName],
  );

  /**
   * Manually select a "weekId".
   * Update URL query param for a selected "weekId".
   *
   * @param {ICurrentWeek} args
   */
  const selectWeek = useCallback(
    (weekId: number) => {
      const params = new URLSearchParams(history.location.search);
      params.set('weekid', String(weekId));
      history.push({ search: params.toString() });
    },
    [history],
  );

  /**
   * Manually set the days span to show for a week.
   * Update URL query param for a selected "days".
   *
   * @param {number} days
   */
  const selectDays = useCallback(
    (days: number) => {
      const params = new URLSearchParams(history.location.search);
      params.set('days', String(days));
      history.push({ search: params.toString() });
    },
    [history],
  );

  /**
   * Manually set the day offset
   * Update URL query param for a selected "offset".
   *
   * @param {offset} days
   */
  const selectDayOffset = useCallback(
    (offset: WeekDays) => {
      const params = new URLSearchParams(history.location.search);
      params.set('offset', String(offset));
      history.push({ search: params.toString() });
    },
    [history],
  );

  /**
   * Manually set the start day index span to show for less than a week.
   * Update URL query param for a selected "offset".
   * goLeft false meaning go right (increment)
   *
   * @param {number} days
   */
  const selectStartDayIndex = useCallback(
    (diff: number) => {
      const params = new URLSearchParams(history.location.search);

      if (!_getOffsetFromURL) {
        diff < 0 ? params.set('offset', String(WeekDays.Monday)) : params.set('offset', String(WeekDays.Tuesday));

        history.push({ search: params.toString() });
        return;
      }

      const newOffset = _getOffsetFromURL + diff;

      switch (_getDaysFromURL) {
        case CalendarDisplayRange.ONE_DAY:
          newOffset >= WeekDays.Monday && newOffset <= WeekDays.Sunday && params.set('offset', String(newOffset));
          break;
        case CalendarDisplayRange.THREE_DAYS:
          _getOffsetFromURL > WeekDays.Friday
            ? params.set('offset', String(WeekDays.Thursday))
            : newOffset >= WeekDays.Monday && newOffset <= WeekDays.Friday && params.set('offset', String(newOffset));
          break;
        default:
          params.delete('offset');
          break;
      }
      history.push({ search: params.toString() });
    },
    [history, _getOffsetFromURL, _getDaysFromURL],
  );

  /**
   * Checks whether a manually selected "weekId" (query param)
   * is equal with the current "weekId" (current week).
   * If there's no "weekId" (query param) selected, the expected week is current week.
   *
   * @return {*}  {boolean}
   */
  const isCurrentWeek = useMemo((): boolean => {
    const weekId = _getWeekIdFromURL;

    if (!weekId) {
      return true;
    }

    return weekId === currentWeek;
  }, [_getWeekIdFromURL, currentWeek]);

  return {
    currentWeek,
    getWeek,
    getWeekBlocks,
    isCurrentWeek,
    selectedWeek: _getSelectedWeek,
    selectedWeekId: _getWeekIdFromURL || currentWeek,
    selectedDays: _getDaysFromURL,
    selectedOffset: _getOffsetFromURL,
    selectDays,
    selectWeek,
    selectStartDayIndex,
    selectDayOffset,
  };
};

export { useWeek };
